"use client";

import type { RefObject } from "react";
import { CHART_INK } from "@/lib/tool-kit/palette";
import {
  CLASS_COLORS,
  CLASS_INK,
  type ClassEntry,
  classesOnDay,
  layoutDay,
} from "@/lib/tool-kit/study/class-schedule";

/*
 * The class schedule maker's timetable, drawn as one self-contained SVG: fills, strokes
 * and fonts are attributes rather than page CSS, so the on-screen grid, the printout and
 * the downloaded SVG and PNG are the same picture. Every word on it comes in through
 * `labels`, from the widget's strings.
 */

const WIDTH = 960;
const GUTTER = 64;
const HEADER = 36;
const HOUR = 56;
const FONT =
  "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

export interface GridLabels {
  /** The SVG's accessible name. */
  title: string;
  /** Seven names each, Monday first. */
  weekdaysLong: readonly string[];
  weekdaysShort: readonly string[];
}

/** The timetable as one self-contained SVG, so print, PNG and SVG all match the screen. */
export function ClassScheduleGrid({
  classes,
  days,
  hours,
  formatTime,
  labels,
  svgRef,
}: {
  classes: readonly ClassEntry[];
  /** Monday-first day positions to draw, in order. */
  days: readonly number[];
  hours: { from: number; to: number };
  formatTime: (minutes: number) => string;
  labels: GridLabels;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const columns = Math.max(1, days.length);
  const columnWidth = (WIDTH - GUTTER) / columns;
  const height = HEADER + (hours.to - hours.from) * HOUR;
  const y = (minutes: number) =>
    HEADER + ((minutes - hours.from * 60) / 60) * HOUR;
  const dayNames =
    columnWidth >= 120 ? labels.weekdaysLong : labels.weekdaysShort;
  return (
    <svg
      aria-label={labels.title}
      className="h-auto w-full rounded-lg border border-border print:border-0"
      fontFamily={FONT}
      height={height}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${WIDTH} ${height}`}
      width={WIDTH}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill={CHART_INK.background} height={height} width={WIDTH} />
      {Array.from(
        { length: hours.to - hours.from + 1 },
        (_, index) => hours.from + index
      ).map((hour) => (
        <g key={hour}>
          <line
            stroke={CHART_INK.grid}
            x1={GUTTER}
            x2={WIDTH}
            y1={y(hour * 60)}
            y2={y(hour * 60)}
          />
          {hour < hours.to ? (
            <text
              fill={CHART_INK.muted}
              fontSize={12}
              textAnchor="end"
              x={GUTTER - 8}
              y={y(hour * 60) + 14}
            >
              {formatTime(hour * 60)}
            </text>
          ) : null}
        </g>
      ))}
      {days.map((day, column) => {
        const left = GUTTER + column * columnWidth;
        return (
          <g key={day}>
            <line
              stroke={CHART_INK.grid}
              x1={left}
              x2={left}
              y1={0}
              y2={height}
            />
            <text
              fill={CHART_INK.text}
              fontSize={14}
              fontWeight={600}
              textAnchor="middle"
              x={left + columnWidth / 2}
              y={23}
            >
              {dayNames[day]}
            </text>
            {layoutDay(classesOnDay(classes, day)).map(
              ({ entry, lane, lanes }) => {
                const width = columnWidth / lanes;
                const x = left + lane * width + 2;
                const top = y(entry.start) + 1;
                const blockHeight = Math.max(
                  18,
                  y(entry.end) - y(entry.start) - 2
                );
                const color = CLASS_COLORS[entry.color] ?? CLASS_COLORS[0];
                const clip = `cs-clip-${day}-${entry.id}`;
                return (
                  <g key={entry.id}>
                    <clipPath id={clip}>
                      <rect
                        height={blockHeight}
                        width={width - 4}
                        x={x}
                        y={top}
                      />
                    </clipPath>
                    <rect
                      fill={color.fill}
                      height={blockHeight}
                      rx={4}
                      stroke={color.edge}
                      width={width - 4}
                      x={x}
                      y={top}
                    />
                    <rect
                      fill={color.edge}
                      height={blockHeight}
                      width={4}
                      x={x}
                      y={top}
                    />
                    <g clipPath={`url(#${clip})`} fill={CLASS_INK}>
                      <text
                        fontSize={13}
                        fontWeight={600}
                        x={x + 10}
                        y={top + 16}
                      >
                        {entry.name}
                      </text>
                      <text fontSize={11} x={x + 10} y={top + 31}>
                        {`${formatTime(entry.start)}–${formatTime(entry.end)}`}
                      </text>
                      {entry.location ? (
                        <text fontSize={11} x={x + 10} y={top + 45}>
                          {entry.location}
                        </text>
                      ) : null}
                    </g>
                  </g>
                );
              }
            )}
          </g>
        );
      })}
    </svg>
  );
}
